import { useEffect, useRef, useState } from 'react';

/**
 * Counts from 0 to `target` once the element scrolls into view.
 * Returns [displayValue, ref] — attach the ref to the element to watch.
 */
export function useCountUp(
  target: number,
  durationMs = 1400,
): [number, React.RefObject<HTMLSpanElement | null>] {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || started.current) return;
      started.current = true;
      io.disconnect();
      const start = performance.now();
      const tick = (now: number): void => {
        const p = Math.min(1, (now - start) / durationMs);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(Math.round(target * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, durationMs]);

  return [value, ref];
}
